/**
 * AutoSyncReviewPage
 * ──────────────────
 * Full-page review surface for an Auto Sync multimodal session, opened from
 * the Deskey Sessions card once transcription + board OCR have finished.
 *
 * Shows the extracted inputs side by side — no AI chat dock:
 *   • Audio transcription (Whisper output)
 *   • Board extracted text (Gemini OCR per board photo)
 *
 * The Smart Note fusion + studio draft are only created when the teacher
 * clicks "Generate Session" at the bottom; the resulting draft then opens
 * in the existing ContentEditor.
 */
import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import {
  ArrowLeft, Mic, ImageIcon, Sparkles, Loader2, AlertTriangle, FileText,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const api = async (path, opts = {}) => {
  const token = localStorage.getItem('desky_token');
  const res = await fetch(`${API}/api/teacher${path}`, {
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json', ...opts.headers },
    ...opts,
  });
  const text = await res.text();
  try { return text ? JSON.parse(text) : {}; }
  catch { return { detail: text || `HTTP ${res.status}`, _status: res.status }; }
};

function SectionCard({ icon: Icon, kicker, title, badge, children, testid }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden"
         style={{ boxShadow: '0 1px 0 rgba(15,23,42,0.04), 0 8px 32px rgba(15,23,42,0.06)' }}
         data-testid={testid}>
      <div className="px-5 py-4 border-b border-zinc-100 flex items-center gap-3">
        <div className="w-9 h-9 rounded-xl flex items-center justify-center text-white" style={{ background: 'linear-gradient(135deg, #34A853 0%, #0d9488 100%)' }}>
          <Icon className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-widest font-bold text-emerald-700">{kicker}</div>
          <div className="text-[15px] font-extrabold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif', letterSpacing: '-0.015em' }}>{title}</div>
        </div>
        {badge}
      </div>
      <div className="p-5">{children}</div>
    </div>
  );
}

const POLL_MS = 3000;
const POLL_MAX_MS = 10 * 60 * 1000; // backend caps fusion at 8 min — stop polling shortly after

// Fusion is server-side and can run for minutes. A single frozen "Generating…"
// reads as a hang, so walk through what the pipeline is actually doing. The
// last stage has no successor — it holds until the draft is ready rather than
// looping, which would look like a stuck progress bar.
export const GENERATE_STAGES = [
  'Reading transcript…',
  'Matching board notes…',
  'Structuring sections…',
  'Writing lesson content…',
  'Fine-tuning…',
  'Almost there…',
];
const STAGE_MS = 6000;

export default function AutoSyncReviewPage({ session, goBack, onGenerated }) {
  const [review, setReview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [stage, setStage] = useState(0);
  const [errorMsg, setErrorMsg] = useState(null);
  const aliveRef = useRef(true);

  // Advance the progress copy while generation runs, stopping on the last
  // stage so it never cycles back and implies restarted work.
  useEffect(() => {
    if (!generating) { setStage(0); return; }
    const t = setInterval(() => {
      setStage(s => (s < GENERATE_STAGES.length - 1 ? s + 1 : s));
    }, STAGE_MS);
    return () => clearInterval(t);
  }, [generating]);

  useEffect(() => {
    aliveRef.current = true;
    return () => { aliveRef.current = false; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api(`/auto-sync/${session.id}/review`);
        if (cancelled) return;
        if (r.session_id) setReview(r);
        else setErrorMsg(r.detail || 'Could not load session');
      } catch { if (!cancelled) setErrorMsg('Could not load session'); }
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [session.id]);

  // ContentEditor has two "not finished yet" screens, and the teacher should
  // see neither on this path:
  //   • no transcript + no framework → "Start with Transcription"
  //   • transcript but no framework   → "Generate Lesson Framework"
  // The draft row is written with `framework: null, framework_status:
  // "generating"` and the framework is authored afterwards by a background
  // task, so a draft_id (and even a transcript) means nothing yet. Only a
  // populated `framework` renders the section list, so that is the gate.
  const hasFramework = (d) => !!(d && d.framework);

  // Wait out `framework_status: generating`. Resolves to the full draft once
  // the framework lands, or null if the server says nothing more is coming
  // (status "none"/error) — in that case the manual framework screen IS the
  // correct destination and we stop blocking on it.
  const waitForFramework = async (draftId, deadline) => {
    while (Date.now() < deadline) {
      if (!aliveRef.current) return null;
      let st;
      try { st = await api(`/studio/framework-status/${draftId}`); }
      catch { await new Promise(r => setTimeout(r, POLL_MS)); continue; }

      if (st.status === 'ready') {
        const full = await api(`/studio/drafts/${draftId}`).catch(() => null);
        if (hasFramework(full)) return full;
        // status flipped before the row was readable — retry shortly
      } else if (st.status !== 'generating') {
        return null; // nothing further will be produced
      }
      await new Promise(r => setTimeout(r, POLL_MS));
    }
    return null;
  };

  const generate = async () => {
    setGenerating(true);
    setErrorMsg(null);
    try {
      const deadline = Date.now() + POLL_MAX_MS;
      const finish = async (draftId, draft) => {
        if (hasFramework(draft)) { onGenerated(draft); return true; }
        const full = await waitForFramework(draftId, deadline);
        if (!aliveRef.current) return true;
        onGenerated(full || draft || { id: draftId });
        return true;
      };

      const r = await api(`/auto-sync/${session.id}/generate-session`, { method: 'POST' });
      if (r.draft_id) {
        toast.success(r.existing ? 'Opening the existing draft.' : 'Session generated — opening the editor.');
        await finish(r.draft_id, r.draft);
        return;
      }
      if (r.status === 'fusing') {
        // Fusion runs server-side in the background (large transcripts can
        // take minutes) — poll the smart-note endpoint until the draft appears,
        // then keep waiting for its framework before handing over.
        while (Date.now() < deadline) {
          await new Promise(res => setTimeout(res, POLL_MS));
          if (!aliveRef.current) return;
          let s;
          try { s = await api(`/auto-sync/${session.id}/smart-note`); }
          catch { continue; } // transient network blip — keep polling
          if (s.draft_id) {
            toast.success('Session generated — opening the editor.');
            await finish(s.draft_id, s.draft);
            return;
          }
          if (s.status === 'error') {
            setErrorMsg(s.error || 'Generation failed. Try again.');
            setGenerating(false);
            return;
          }
        }
        setErrorMsg('Generation is taking longer than expected — please retry in a minute.');
        setGenerating(false);
        return;
      }
      setErrorMsg(r.detail || 'Generation failed. Try again.');
    } catch { setErrorMsg('Generation failed. Try again.'); }
    setGenerating(false);
  };

  const boards = review?.boards || [];
  const boardsWithText = boards.filter(b => (b.text || '').trim() || (b.latex_blocks || []).length > 0);
  const transcript = (review?.transcript || '').trim();
  const title = review?.title_hint || review?.detected_class_name || session.detected_class_name || 'Deskey Session';
  const canGenerate = !loading && !generating && (transcript || boardsWithText.length > 0);

  if (loading) return (
    <div className="p-10 text-center text-[13px] text-gray-400" data-testid="auto-sync-review-loading">Loading session…</div>
  );

  return (
    <div className="p-8 max-w-5xl mx-auto space-y-6" data-testid="auto-sync-review-page">
      {/* Header */}
      <div className="flex items-center gap-4 flex-wrap">
        <button onClick={goBack} className="w-9 h-9 rounded-xl bg-white border border-zinc-200 hover:bg-zinc-50 flex items-center justify-center text-slate-600 transition-all" data-testid="auto-sync-review-back">
          <ArrowLeft className="w-4 h-4" />
        </button>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-emerald-700 mb-1">Auto Sync · Session Review</div>
          <h2 className="text-2xl font-extrabold text-slate-900 truncate" style={{ fontFamily: 'Manrope, sans-serif', letterSpacing: '-0.025em' }}>{title}</h2>
          <p className="text-[13px] text-slate-500 mt-1 font-medium">Review what Deskey heard and read from the boards, then generate the session.</p>
        </div>
        <div className="flex items-center gap-1.5 flex-wrap">
          {review?.subject_label && <span className="text-[10px] font-bold uppercase tracking-[0.12em] px-2 py-0.5 rounded-md bg-indigo-50 text-indigo-700 border border-indigo-100">{review.subject_label}</span>}
          {review?.grade_label && <span className="text-[10px] font-bold uppercase tracking-[0.12em] px-2 py-0.5 rounded-md bg-emerald-50 text-emerald-700 border border-emerald-100">{review.grade_label}</span>}
        </div>
      </div>

      {/* Audio transcription */}
      <SectionCard
        icon={Mic}
        kicker="1 · Audio transcription"
        title="What was said in class"
        badge={transcript ? <span className="text-[10px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-100 px-2.5 py-0.5 rounded-full">{transcript.split(/\s+/).length} words</span> : null}
        testid="auto-sync-review-transcript">
        {transcript ? (
          <p className="text-[13px] text-slate-700 leading-relaxed whitespace-pre-wrap">{transcript}</p>
        ) : (
          <div className="rounded-xl border-2 border-dashed border-zinc-200 py-6 text-center text-[12px] text-slate-500">
            No transcript was produced for this session.
          </div>
        )}
      </SectionCard>

      {/* Board extracted text */}
      <SectionCard
        icon={ImageIcon}
        kicker="2 · Board extracted text"
        title="What was written on the boards"
        badge={boards.length > 0 ? <span className="text-[10px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-100 px-2.5 py-0.5 rounded-full">{boards.length} board{boards.length === 1 ? '' : 's'}</span> : null}
        testid="auto-sync-review-boards">
        {boards.length === 0 ? (
          <div className="rounded-xl border-2 border-dashed border-zinc-200 py-6 text-center text-[12px] text-slate-500">
            No board photos were added to this session.
          </div>
        ) : (
          <div className="space-y-3">
            {boards.map((b, i) => (
              <div key={b.capture_id || i} className="rounded-xl border border-zinc-200 overflow-hidden" data-testid={`auto-sync-review-board-${i}`}>
                <div className="px-3 py-2 bg-zinc-50 flex items-center gap-2">
                  <FileText className="w-3.5 h-3.5 text-slate-500" />
                  <span className="text-[11px] uppercase tracking-widest font-bold text-slate-600 flex-1">Board {i + 1}</span>
                  {b.ocr_status !== 'done' && (
                    <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-100">{b.ocr_status === 'failed' ? 'OCR failed' : 'OCR pending'}</span>
                  )}
                </div>
                <div className="p-3 bg-white">
                  {(b.text || '').trim() ? (
                    <p className="text-[13px] text-slate-700 leading-relaxed whitespace-pre-wrap">{b.text}</p>
                  ) : (
                    <p className="text-[12px] text-slate-400">{b.ocr_status === 'failed' ? (b.error || 'Could not read this board.') : 'No readable text on this board.'}</p>
                  )}
                  {(b.latex_blocks || []).length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {b.latex_blocks.map((eq, j) => (
                        <code key={j} className="text-[11px] font-mono px-2 py-1 rounded-md bg-emerald-50 text-emerald-900 border border-emerald-200">{eq}</code>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </SectionCard>

      {errorMsg && (
        <div className="rounded-2xl border border-red-200 bg-red-50 p-4 flex items-center gap-3" data-testid="auto-sync-review-error">
          <AlertTriangle className="w-4 h-4 text-red-600" />
          <div className="text-[13px] font-bold text-red-900">{errorMsg}</div>
        </div>
      )}

      {/* Generate Session */}
      <div className="flex flex-wrap items-center justify-end gap-2 pt-1 pb-6">
        <button onClick={goBack} className="h-11 px-5 rounded-xl bg-white border border-zinc-200 text-[12px] font-bold hover:bg-zinc-50 transition-all" data-testid="auto-sync-review-cancel">
          Back to sessions
        </button>
        <button
          onClick={generate}
          disabled={!canGenerate}
          data-testid="auto-sync-review-generate"
          className="h-11 px-6 rounded-xl text-white text-[13px] font-bold inline-flex items-center gap-2 disabled:opacity-50 transition-all shadow-[0_8px_24px_rgba(52,168,83,0.28)] hover:shadow-[0_10px_28px_rgba(52,168,83,0.4)] active:scale-[0.98]"
          style={{ background: 'linear-gradient(135deg, #34A853 0%, #0d9488 100%)' }}>
          {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
          <span data-testid="auto-sync-generate-label">
            {generating ? GENERATE_STAGES[stage] : 'Generate Session'}
          </span>
        </button>
      </div>
    </div>
  );
}
